﻿import React, { useState, useEffect } from 'react';
import { HplcImpurity } from '../models/hplc-impurity';
import { UOM } from '../models/uom';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import HplcImpuritiesComponent from './HplcImpuritiesComponent';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import DataGrid, {
    Column,
    Selection,
    Paging,
    ColumnFixing,
    SearchPanel,
    Toolbar,
    Summary,
    TotalItem,
    Item as ToolBarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

const decimalFormat = {
    type: "fixedPoint",
    precision: 4
}

let key = 0;

export interface Props {
    benchSheetId: string;
    uoms?: UOM[];
    isReadOnly?: boolean;
    datagridHeight?: string;
}

export default function HplcImpuritiesDataComponent(props: Props) {
    const [impurities, setImpurities] =
        useState<HplcImpurity[]>();

    const [hplcImpuritiesDialogVisible, setHplcImpuritiesDialogVisible] =
        useState<boolean>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();
           
    useEffect(() => {
        getHplcImpurities();
    }, [props.benchSheetId]);

    key++;

    return (
        <div>
            <div className="pw-datagrid">
                <DataGrid
                    height={props.datagridHeight ? props.datagridHeight : "100%"}
                    keyExpr="hplcImpurityDataId"
                    wordWrapEnabled={true}
                    width="100%"
                    dataSource={impurities ? impurities : []}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true}>

                    <SearchPanel visible={true} />
                    <LoadPanel enabled={true} />
                    <ColumnFixing enabled={true} />
                    <Selection mode={'single'} />
                    <Paging enabled={false} />

                    <Column width={'3em'} caption="" cellRender={impuritiesInfoButtonTemplate} />

                    <Column dataField={'elution'} dataType="number" width={'8em'} alignment='center' />
                    <Column dataField={'impurity'} width={'50%'} />
                    <Column dataField={'result'} dataType="number" format={decimalFormat} width={'25%'} />
                    <Column dataField={'retentionTime'} dataType="number" format={decimalFormat} width={'25%'} />

                    <Summary>
                        <TotalItem column="result"
                            displayFormat="Average HPLC Impurities: {0}"
                            valueFormat={{ type: "fixedPoint", precision: 4 }}
                            summaryType="avg" />
                    </Summary>

                    <Toolbar>
                        <ToolBarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Impurities</div>
                            </div>
                        </ToolBarItem>

                        <ToolBarItem location='after'>
                            <Button type='normal' hint='edit impurities' icon="inserttable"
                                width={'2.1em'} disabled={props.isReadOnly}
                                onClick={showHplcImpuritiesDialog} />
                        </ToolBarItem>
                    </Toolbar>
                </DataGrid>
            </div>

            {hplcImpuritiesDialogVisible &&
                <DialogPopup
                    visible={hplcImpuritiesDialogVisible}
                    onHiding={hideHplcImpuritiesDialog}
                    dragEnabled={false}
                    fullScreen={true}
                    hideOnOutsideClick={false}
                    title="HPLC Impurities">

                    <HplcImpuritiesComponent key={`hplcimpurities${key}`}
                        benchSheetId={props.benchSheetId}
                        onClose={hideHplcImpuritiesDialog} />

                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );
        
    function impuritiesInfoButtonTemplate(e: any) {
        return (
            <div className="bs-icon-group-together">
                <Button
                    className="bs-icon-custom-edit"
                    icon="info"
                    onClick={() => onImpuritiesInfoButtonClick(e)}>
                </Button>
            </div>
        );
    }

    function onImpuritiesInfoButtonClick(e: any) {
        if (impurities) {
            let entry = impurities.find(p => p.hplcImpurityDataId === e.key)
            if (entry) {

                let itemBasicInitInformation: ItemBasicInformation = {
                    rowPointer: entry.hplcImpurityDataId,
                    createTimestamp: entry.createTimestamp,
                    lastModifiedTimestamp: entry.modifyTimestamp,
                    createUser: entry.createUser,
                    modifyUser: entry.modifyUser
                };

                setItemBasicInitInformation(itemBasicInitInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    async function getHplcImpurities() {
        await CrudFunctions.getData(`benchsheets/hplcimpuritiesbybenchsheetid/${props.benchSheetId}`)
            .then((data) => {
                setImpurities(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving HPLC Impurities");
            });
    }

    //show/hide

    function showHplcImpuritiesDialog() {
        setHplcImpuritiesDialogVisible(true);
    }

    function hideHplcImpuritiesDialog() {
        getHplcImpurities();
        setHplcImpuritiesDialogVisible(false);
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(true);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
